const STORAGE_KEY = 'bx-dual-pane-prefs';

export const MIN_PANEL_PX = 280;
export const STACKED_PANEL_MIN = 520;
export const DIVIDER_PX = 6;
export const DEFAULT_SPLIT_RATIO = 0.6;

export const ORIENTATION = {
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical',
};

export const PANEL_ORDER = {
  TOWNHALL_FIRST: 'townhall-first',
  MARKET_FIRST: 'market-first',
};

const DEFAULT_PREFS = {
  orientation: ORIENTATION.HORIZONTAL,
  order: PANEL_ORDER.TOWNHALL_FIRST,
  splitRatio: DEFAULT_SPLIT_RATIO,
};

export const getStackedBudget = () => {
  const floor = STACKED_PANEL_MIN * 2 + DIVIDER_PX;
  if (typeof window === 'undefined') {
    return floor;
  }
  return Math.max(floor, Math.round(window.innerHeight * 0.82));
};

const clampRatio = (ratio, containerSize, minPanelPx = MIN_PANEL_PX) => {
  if (!containerSize || containerSize <= 0) {
    return DEFAULT_SPLIT_RATIO;
  }

  const minRatio = minPanelPx / containerSize;
  const maxRatio = 1 - minPanelPx / containerSize;

  if (maxRatio <= minRatio) {
    return 0.5;
  }

  if (ratio <= minRatio / 2) {
    return 0;
  }
  if (ratio >= 1 - minRatio / 2) {
    return 1;
  }

  return Math.min(maxRatio, Math.max(minRatio, ratio));
};

export const readDualPanePrefs = () => {
  if (typeof window === 'undefined') {
    return { ...DEFAULT_PREFS };
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { ...DEFAULT_PREFS };
    }
    const parsed = JSON.parse(raw);
    return {
      orientation:
        parsed.orientation === ORIENTATION.VERTICAL
          ? ORIENTATION.VERTICAL
          : ORIENTATION.HORIZONTAL,
      order:
        parsed.order === PANEL_ORDER.MARKET_FIRST
          ? PANEL_ORDER.MARKET_FIRST
          : PANEL_ORDER.TOWNHALL_FIRST,
      splitRatio:
        typeof parsed.splitRatio === 'number' && parsed.splitRatio >= 0 && parsed.splitRatio <= 1
          ? parsed.splitRatio
          : DEFAULT_SPLIT_RATIO,
    };
  } catch (e) {
    return { ...DEFAULT_PREFS };
  }
};

export const writeDualPanePrefs = prefs => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch (e) {
    // ignore quota / private mode
  }
};

export const ratioFromPointer = ({ containerRect, clientX, clientY, orientation }) => {
  if (orientation === ORIENTATION.HORIZONTAL) {
    const usable = containerRect.width - DIVIDER_PX;
    if (usable <= 0) {
      return DEFAULT_SPLIT_RATIO;
    }
    const offset = clientX - containerRect.left;
    return clampRatio(offset / usable, usable);
  }

  const usable = (containerRect.height || getStackedBudget()) - DIVIDER_PX;
  if (usable <= 0) {
    return DEFAULT_SPLIT_RATIO;
  }
  const offset = clientY - containerRect.top;
  return clampRatio(offset / usable, usable, STACKED_PANEL_MIN);
};

export const getVerticalPanelHeights = (splitRatio, budget = getStackedBudget()) => {
  const usable = budget - DIVIDER_PX;

  if (splitRatio <= 0) {
    return { firstHeight: 0, secondHeight: usable };
  }
  if (splitRatio >= 1) {
    return { firstHeight: usable, secondHeight: 0 };
  }

  let firstHeight = Math.round(splitRatio * usable);
  let secondHeight = usable - firstHeight;

  if (firstHeight < STACKED_PANEL_MIN) {
    firstHeight = STACKED_PANEL_MIN;
    secondHeight = usable - firstHeight;
  }
  if (secondHeight < STACKED_PANEL_MIN) {
    secondHeight = STACKED_PANEL_MIN;
    firstHeight = usable - secondHeight;
  }

  if (firstHeight < STACKED_PANEL_MIN) {
    return { firstHeight: usable, secondHeight: 0 };
  }
  if (secondHeight < STACKED_PANEL_MIN) {
    return { firstHeight: 0, secondHeight: usable };
  }

  return { firstHeight, secondHeight };
};

export { DEFAULT_PREFS, clampRatio };
